import { useState, useMemo, useRef, useEffect } from 'react';
import { Calendar, Clock, Plus, GripVertical, CheckCircle2, Users, Sun } from 'lucide-react';
import { formatEventTime } from '../../hooks/useGoogleCalendar';
import { calculateBlockCapacity, formatRemainingCapacity, getFillColor } from '../../utils/timeBlockCapacity';
import TimeBlockModal from '../TimeBlockModal';

// Timeline constants
const HOUR_HEIGHT = 80; // px per hour (increased from 60 for better readability)
const MIN_TIMELINE_START = 6;  // Don't show earlier than 6 AM
const MAX_TIMELINE_END = 20;   // Don't show later than 8 PM

// Block colors matching Schedule tab
const BLOCK_COLORS = {
  'office time': '#C45D3A',
  'plan & organize': '#7C5CBF',
  'projects': '#3B82C4',
  'delegate and inspect': '#2D8A5F',
  'morning start': '#C44D7A',
  'eod wrap-up': '#5B6BD4'
};

function getBlockColor(name) {
  const lower = (name || '').toLowerCase();
  return BLOCK_COLORS[lower] || '#6B7280';
}

// Convert hex color to light tint for backgrounds
function getLightBackground(hexColor) {
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const lightR = Math.round(r * 0.08 + 255 * 0.92);
  const lightG = Math.round(g * 0.08 + 255 * 0.92);
  const lightB = Math.round(b * 0.08 + 255 * 0.92);
  return `rgb(${lightR}, ${lightG}, ${lightB})`;
}

// Get position and height for timeline (with dynamic bounds)
function getTimelinePosition(event, timelineStart, totalHours) {
  if (!event.start?.dateTime) return null;

  const start = new Date(event.start.dateTime);
  const end = new Date(event.end?.dateTime);

  const startHour = start.getHours() + start.getMinutes() / 60;
  const endHour = end.getHours() + end.getMinutes() / 60;

  const timelineEnd = timelineStart + totalHours;
  const clampedStart = Math.max(startHour, timelineStart);
  const clampedEnd = Math.min(endHour, timelineEnd);

  if (clampedEnd <= clampedStart) return null;

  const top = ((clampedStart - timelineStart) / totalHours) * 100;
  const height = ((clampedEnd - clampedStart) / totalHours) * 100;

  return { top, height, startHour, endHour };
}

// Calculate columns for overlapping events
function calculateEventColumns(events, timelineStart, totalHours) {
  const positioned = events
    .map(event => ({ event, pos: getTimelinePosition(event, timelineStart, totalHours) }))
    .filter(e => e.pos !== null)
    .sort((a, b) => a.pos.startHour - b.pos.startHour);

  const columns = [];

  positioned.forEach(({ event, pos }) => {
    let placed = false;

    for (let col = 0; col < columns.length; col++) {
      const lastInCol = columns[col][columns[col].length - 1];
      if (lastInCol.pos.endHour <= pos.startHour) {
        columns[col].push({ event, pos });
        placed = true;
        break;
      }
    }

    if (!placed) {
      columns.push([{ event, pos }]);
    }
  });

  // Assign column index and total columns to each event
  const eventColumns = new Map();
  columns.forEach((col, colIndex) => {
    col.forEach(({ event }) => {
      eventColumns.set(event.id, { column: colIndex, totalColumns: columns.length });
    });
  });

  return eventColumns;
}

// Format tomorrow's date
function formatTomorrowDate(dateStr) {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric'
  });
}

// Generate time labels for given range
function generateTimeLabels(timelineStart, timelineEnd) {
  const labels = [];
  for (let h = timelineStart; h <= timelineEnd; h++) {
    const hour = h % 12 || 12;
    const ampm = h < 12 ? 'a' : 'p';
    labels.push({ hour: h, label: `${hour}${ampm}` });
  }
  return labels;
}

export default function ScheduleReview({
  tomorrowDate,
  allEvents = [],
  timeBlocks = [],
  unassignedTasks = [],
  getTasksForBlock,
  onAssignTask,
  onCreateTimeBlock,
  timeBlockNames = []
}) {
  const [draggedTask, setDraggedTask] = useState(null);
  const [dropTargetBlock, setDropTargetBlock] = useState(null);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const timelineRef = useRef(null);

  // Check if event is a time block
  const isTimeBlock = (event) => {
    return timeBlockNames.some(name =>
      event.summary?.toLowerCase().includes(name.toLowerCase())
    );
  };

  // Filter to only timed events (not all-day)
  const timedEvents = useMemo(() =>
    allEvents.filter(e => e.start?.dateTime),
    [allEvents]
  );

  // Calculate dynamic timeline bounds based on actual events
  const { timelineStart, timelineEnd, totalHours } = useMemo(() => {
    if (timedEvents.length === 0) {
      // Default to 8 AM - 6 PM if no events
      return { timelineStart: 8, timelineEnd: 18, totalHours: 10 };
    }

    let earliest = 24;
    let latest = 0;

    timedEvents.forEach(event => {
      const start = new Date(event.start.dateTime);
      const end = new Date(event.end.dateTime);
      const startHour = start.getHours() + start.getMinutes() / 60;
      const endHour = end.getHours() + end.getMinutes() / 60;

      earliest = Math.min(earliest, startHour);
      latest = Math.max(latest, endHour);
    });

    // Add 1-hour padding and clamp to reasonable bounds
    const start = Math.max(MIN_TIMELINE_START, Math.floor(earliest) - 1);
    const end = Math.min(MAX_TIMELINE_END, Math.ceil(latest) + 1);

    return {
      timelineStart: start,
      timelineEnd: end,
      totalHours: end - start
    };
  }, [timedEvents]);

  // Generate time labels for the dynamic range
  const timeLabels = useMemo(() =>
    generateTimeLabels(timelineStart, timelineEnd),
    [timelineStart, timelineEnd]
  );

  // Calculate event columns for overlap handling
  const eventColumns = useMemo(() =>
    calculateEventColumns(timedEvents, timelineStart, totalHours),
    [timedEvents, timelineStart, totalHours]
  );

  // Auto-scroll to 8 AM on mount
  useEffect(() => {
    if (timelineRef.current && timedEvents.length > 0) {
      const targetHour = 8;
      const scrollTop = ((targetHour - timelineStart) / totalHours) * (totalHours * HOUR_HEIGHT);
      requestAnimationFrame(() => {
        if (timelineRef.current) {
          timelineRef.current.scrollTop = Math.max(0, scrollTop - 20);
        }
      });
    }
  }, [timedEvents.length, timelineStart, totalHours]);

  // Drag handlers
  const handleDragStart = (e, task) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggedTask(null);
    setDropTargetBlock(null);
  };

  const handleDragOver = (e, blockId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTargetBlock(blockId);
  };

  const handleDragLeave = () => {
    setDropTargetBlock(null);
  };

  const handleDrop = async (e, block) => {
    e.preventDefault();
    if (draggedTask && onAssignTask) {
      await onAssignTask(draggedTask.id, block.id, block.summary, tomorrowDate);
    }
    setDraggedTask(null);
    setDropTargetBlock(null);
  };

  // Handle saving time block from modal
  const handleSaveBlock = async (formData) => {
    if (!onCreateTimeBlock) return;
    await onCreateTimeBlock(
      formData.startTime,
      formData.endTime,
      formData.name
    );
  };

  // Empty state
  if (allEvents.length === 0 && unassignedTasks.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
        <Calendar size={48} className="text-text-muted mb-4 opacity-50" />
        <h3 className="text-lg font-semibold text-text-primary mb-2">
          No Events Tomorrow
        </h3>
        <p className="text-sm text-text-tertiary max-w-xs">
          Your calendar is empty for tomorrow. Events and time blocks will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-surface-card border-b border-border flex items-center gap-2">
        <Sun size={18} className="text-amber-400" />
        <span className="text-sm font-semibold text-text-primary">
          {formatTomorrowDate(tomorrowDate)}
        </span>
        <span className="text-xs text-text-tertiary ml-2">
          {timeBlocks.length} time block{timeBlocks.length !== 1 ? 's' : ''} · {unassignedTasks.length} unassigned
        </span>
        <div className="flex-1" />
        <button
          onClick={() => setShowBlockModal(true)}
          className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-accent bg-accent/10 rounded-lg border-none cursor-pointer hover:bg-accent/20 transition-all"
        >
          <Plus size={14} />
          Add Block
        </button>
      </div>

      {/* Two-column layout - left is 20% wider */}
      <div className="flex-1 overflow-hidden flex">
        {/* Left column: Schedule timeline (wider) */}
        <div ref={timelineRef} className="flex-[1.2] overflow-y-auto border-r border-border">
          <div className="flex min-h-full">
            {/* Time labels column */}
            <div className="w-12 flex-shrink-0 border-r border-border bg-surface-base">
              {timeLabels.map(({ hour, label }) => (
                <div
                  key={hour}
                  className="flex items-start justify-end pr-2 pt-0 text-[11px] text-text-tertiary"
                  style={{ height: `${HOUR_HEIGHT}px` }}
                >
                  {label}
                </div>
              ))}
            </div>

            {/* Timeline area */}
            <div
              className="flex-1 relative bg-surface-card"
              style={{ height: `${totalHours * HOUR_HEIGHT}px` }}
            >
              {/* Hour lines */}
              {timeLabels.map(({ hour }) => (
                <div
                  key={hour}
                  className="absolute left-0 right-0 border-t border-border"
                  style={{ top: `${((hour - timelineStart) / totalHours) * 100}%` }}
                />
              ))}

              {/* Events */}
              {timedEvents.map((event) => {
                const pos = getTimelinePosition(event, timelineStart, totalHours);
                if (!pos) return null;

                const isBlock = isTimeBlock(event);
                const blockColor = getBlockColor(event.summary);
                const tasks = isBlock && getTasksForBlock ? getTasksForBlock(event.id) : [];
                const capacity = isBlock ? calculateBlockCapacity(event, tasks) : null;
                const isDropTarget = dropTargetBlock === event.id;

                // Get column info for overlapping events
                const columnInfo = eventColumns.get(event.id) || { column: 0, totalColumns: 1 };
                const columnWidth = 100 / columnInfo.totalColumns;
                const leftOffset = columnInfo.column * columnWidth;

                // Calculate if short event
                const durationMinutes = (pos.endHour - pos.startHour) * 60;
                const isShortEvent = durationMinutes <= 30;

                // Calculate minimum height as percentage to prevent overlap
                // 36px minimum / total timeline height in pixels * 100
                const minHeightPercent = (36 / (totalHours * HOUR_HEIGHT)) * 100;
                const effectiveHeight = Math.max(pos.height, minHeightPercent);

                return (
                  <div
                    key={event.id}
                    className={`group absolute rounded-lg overflow-visible transition-all duration-200 ${
                      isDropTarget ? 'ring-2 ring-accent shadow-lg z-50' : 'hover:shadow-lg hover:z-40'
                    }`}
                    style={{
                      top: `calc(${pos.top}% + 2px)`,
                      height: `calc(${effectiveHeight}% - 4px)`,
                      left: `calc(${leftOffset}% + 8px)`,
                      width: `calc(${columnWidth}% - 16px)`,
                      backgroundColor: isDropTarget ? 'rgb(249, 115, 22, 0.15)' : getLightBackground(blockColor),
                      borderLeft: `3px solid ${blockColor}`
                    }}
                    onDragOver={isBlock ? (e) => handleDragOver(e, event.id) : undefined}
                    onDragLeave={isBlock ? handleDragLeave : undefined}
                    onDrop={isBlock ? (e) => handleDrop(e, event) : undefined}
                  >
                    {/* Capacity fill indicator for time blocks */}
                    {isBlock && capacity && (
                      <div
                        className="absolute inset-0 rounded-r-lg pointer-events-none"
                        style={{
                          background: `linear-gradient(to right, ${getFillColor(capacity.fillPercentage, capacity.isOverbooked)}25 ${Math.min(capacity.fillPercentage, 100)}%, transparent ${Math.min(capacity.fillPercentage, 100)}%)`,
                          zIndex: 0
                        }}
                      />
                    )}

                    {/* Event content */}
                    <div className={`relative px-2 py-1.5 h-full overflow-hidden flex ${isShortEvent ? 'items-center gap-2' : 'flex-col justify-center'}`}>
                      <div className="flex items-center gap-1.5">
                        {!isBlock && <Users size={11} className="text-gray-500 flex-shrink-0" />}
                        <span className={`font-semibold text-gray-800 truncate ${isShortEvent ? 'text-[11px]' : 'text-xs'}`}>
                          {event.summary || 'Untitled'}
                        </span>
                      </div>
                      {!isShortEvent && (
                        <span className="text-[10px] text-gray-500 truncate">
                          {formatEventTime(event)}
                        </span>
                      )}

                      {/* Drop indicator */}
                      {isDropTarget && (
                        <span className="text-[10px] text-accent font-semibold">
                          Drop to assign
                        </span>
                      )}
                    </div>

                    {/* Hover tooltip */}
                    {isBlock && !draggedTask && (
                      <div className="absolute left-full ml-2 top-0 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[100] pointer-events-none">
                        <div
                          className="bg-white rounded-lg shadow-xl border border-border p-3 min-w-[220px] max-w-[280px]"
                          style={{ borderLeft: `4px solid ${blockColor}` }}
                        >
                          <div className="flex items-start gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="text-sm font-semibold text-gray-800">
                                  {event.summary || 'Untitled'}
                                </span>
                                <span
                                  className="text-[9px] px-1.5 py-0.5 rounded font-medium text-white"
                                  style={{ backgroundColor: blockColor }}
                                >
                                  Block
                                </span>
                              </div>
                              <div className="text-xs text-gray-600 mt-1">
                                {formatEventTime(event)}
                              </div>
                            </div>
                          </div>

                          {/* Tasks list */}
                          {tasks.length > 0 && capacity && (
                            <div className="mt-2 pt-2 border-t border-gray-100">
                              {/* Capacity summary */}
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-[10px] text-gray-500">
                                  {capacity.scheduledMinutes} / {capacity.totalMinutes} min
                                </span>
                                <span className={`text-[10px] font-medium ${
                                  capacity.isOverbooked ? 'text-red-500' : 'text-green-600'
                                }`}>
                                  {formatRemainingCapacity(capacity.remainingMinutes)}
                                </span>
                              </div>
                              {/* Task list */}
                              <div className="max-h-32 overflow-y-auto space-y-1 pr-1">
                                {tasks.map(task => (
                                  <div key={task.id} className="flex items-center gap-1.5 text-[11px]">
                                    <CheckCircle2
                                      size={12}
                                      className={task.completedAt ? 'text-green-600' : 'text-gray-300'}
                                    />
                                    <span className={`flex-1 truncate ${task.completedAt ? 'line-through text-gray-400' : 'text-gray-600'}`}>
                                      {task.title}
                                    </span>
                                    <span className="text-[9px] text-gray-400 flex-shrink-0">
                                      {task.timeEstimate || '15'}m
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Empty block hint */}
                          {tasks.length === 0 && (
                            <div className="mt-2 pt-2 border-t border-gray-100">
                              <p className="text-[10px] text-gray-400 italic">
                                No tasks assigned yet. Drag tasks here to schedule them.
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Empty state for timeline */}
              {timedEvents.length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <p className="text-sm text-text-tertiary">
                    No events scheduled
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right column: Unassigned tasks (narrower) */}
        <div className="flex-1 overflow-y-auto p-4 bg-surface-base">
          <div className="mb-3">
            <h4 className="text-xs font-semibold text-text-secondary uppercase tracking-wide">
              Unassigned Tasks
            </h4>
            <p className="text-[10px] text-text-tertiary mt-0.5">
              Drag to schedule
            </p>
          </div>

          <div className="space-y-2">
            {unassignedTasks.map(task => (
              <div
                key={task.id}
                draggable
                onDragStart={(e) => handleDragStart(e, task)}
                onDragEnd={handleDragEnd}
                className={`
                  p-3 rounded-xl border bg-surface-card cursor-grab active:cursor-grabbing
                  transition-all hover:shadow-md hover:border-border-strong
                  ${draggedTask?.id === task.id ? 'opacity-50 scale-95' : 'border-border'}
                `}
              >
                <div className="flex items-start gap-2">
                  <GripVertical size={14} className="text-text-muted mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-text-primary truncate">
                      {task.title}
                    </p>
                    {task.timeEstimate && (
                      <p className="text-[10px] text-text-tertiary mt-0.5 flex items-center gap-1">
                        <Clock size={10} />
                        {task.timeEstimate} min
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {unassignedTasks.length === 0 && (
              <div className="text-center py-8">
                <CheckCircle2 size={32} className="text-success mx-auto mb-2 opacity-50" />
                <p className="text-xs text-text-tertiary">
                  All tasks assigned!
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Time Block Modal */}
      <TimeBlockModal
        isOpen={showBlockModal}
        onClose={() => setShowBlockModal(false)}
        onSave={handleSaveBlock}
        selectedDate={new Date(tomorrowDate + 'T00:00:00')}
      />
    </div>
  );
}
